import React, { lazy } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PrivateRoute from '~/components/PrivateRoute';
import useAssumeOrganizationOnAppInit from '~/hooks/use-assume-organization-on-app-init';
import useEvent from '~/hooks/use-events';
import useHotjar from '~/hooks/use-hotjar';
import CountriesServicePage from '~/pages/Admin/CountriesService';
import NetworkServicePage from '~/pages/Admin/NetworkService';
import NotFound from '~/pages/NotFound';
import FakeErrorPage from '../ErrorBoundary/FakeErrorPage';

const Organization = lazy(() => import(/* webpackChunkName: "organization" */ '~/pages/Admin/Organization'));
const Organizations = lazy(() => import(/* webpackChunkName: "organizations" */ '~/pages/Admin/Organizations'));
const Pricelists = lazy(() => import(/* webpackChunkName: "pricelists" */ '~/pages/Admin/Pricelists'));
const ElevatedUsers = lazy(() => import(/* webpackChunkName: "elevatedUsers" */ '~/pages/Admin/ElevatedUsers'));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ '~/pages/Dashboard'));
const Sims = lazy(() => import(/* webpackChunkName: "sims" */ '~/pages/Sims'));
const Usage = lazy(() => import(/* webpackChunkName: "usage" */ '~/pages/Usage'));
const Invoices = lazy(() => import(/* webpackChunkName: "invoices" */ '~/pages/Invoices'));
const AuditLogs = lazy(() => import(/* webpackChunkName: "usage" */ '~/pages/AuditLogs'));
const Connectors = lazy(() => import(/* webpackChunkName: "connectors" */ '~/pages/Connectors'));
const NetworkLists = lazy(() => import(/* webpackChunkName: "networklists" */ '~/pages/NetworkLists'));
const Tags = lazy(() => import(/* webpackChunkName: "tags" */ '~/pages/Tags'));
const Users = lazy(() => import(/* webpackChunkName: "users" */ '~/pages/Users'));
const Webhooks = lazy(() => import(/* webpackChunkName: "webhooks" */ '~/pages/Webhooks'));
const ErrorLog = lazy(() => import(/* webpackChunkName: "errorLog" */ '~/pages/ErrorLog'));
const ApiKeys = lazy(() => import(/* webpackChunkName: "apiKeys" */ '~/pages/ApiKeys'));

function PrivateRoutes() {
  useAssumeOrganizationOnAppInit();
  useHotjar();
  useEvent();

  return (
    <Switch>
      <Route
        path='/network-whitelist'
        exact
      >
        <Redirect to='/network-lists' />
      </Route>

      <Route
        path='/dashboard'
        exact
      >
        <Dashboard />
      </Route>

      <Route path='/sims'>
        <Sims />
      </Route>

      <Route
        path='/usage'
        exact
      >
        <Usage />
      </Route>

      <PrivateRoute
        requiredRoles={['owner', 'demigod', 'god']}
        path='/invoices'
      >
        <Invoices />
      </PrivateRoute>

      <Route path='/connectors'>
        <Connectors />
      </Route>

      <Route path='/network-lists'>
        <NetworkLists />
      </Route>

      <Route path='/api-keys'>
        <ApiKeys />
      </Route>

      <PrivateRoute
        requiredRoles={['admin', 'owner', 'god']}
        path='/tags'
      >
        <Tags />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['admin', 'owner', 'god']}
        path='/users'
      >
        <Users />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['owner', 'god']}
        path='/audit-logs'
      >
        <AuditLogs />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['admin', 'owner', 'god']}
        path='/webhooks'
      >
        <Webhooks />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['admin', 'owner', 'god']}
        path='/error-log'
      >
        <ErrorLog />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['demigod', 'god']}
        allowOrgGod
        path='/admin'
        exact
      >
        <Redirect to={{ pathname: '/admin/organizations' }} />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['god']}
        path='/admin/pricelists'
        exact
      >
        <Pricelists />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['demigod', 'god']}
        allowOrgGod
        path='/admin/organizations'
        exact
      >
        <Organizations />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['god']}
        path='/admin/network-service'
        exact
      >
        <NetworkServicePage />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['god']}
        path='/admin/countries-service'
        exact
      >
        <CountriesServicePage />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['god']}
        path='/admin/elevated-users'
        exact
      >
        <ElevatedUsers />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['god']}
        allowOrgGod
        path='/admin/organizations/:organizationId'
      >
        <Organization />
      </PrivateRoute>

      <PrivateRoute
        requiredRoles={['demigod', 'god']}
        path='/fake-error-page'
      >
        <FakeErrorPage />
      </PrivateRoute>

      <Route path='*'>
        <NotFound />
      </Route>
    </Switch>
  );
}

export default PrivateRoutes;
